import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../config';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Lottie from 'lottie-react';
import loaderAnimation from '../css/loader2.json';
import '../css/SaleChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const SaleChart = ({ interval = 'daily' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchData = useCallback(async (interval) => {
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');
    const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));

    if (!selectedCompany || !selectedCompany.CompCode) {
      setError('Company code is missing or invalid');
      setLoading(false);
      return;
    }

    const compCode = selectedCompany.CompCode;

    if (!userId || !accessToken) {
      setError('Missing userId or accessToken in localStorage');
      setLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // Set timeout to 90 seconds

      let apiUrls = [];
      switch (interval) {
        case 'daily':
          apiUrls = [`${API_BASE_URL}/api/DailySummary?CompCode=${compCode}&type=sales`];
          break;
        case 'weekly':
          apiUrls = [
            `${API_BASE_URL}/api/WeeklySummary?CompCode=${compCode}&type=sales`  // Weekly data (6 weeks)
          ];
          break;
        case 'monthly':
          apiUrls = [
            `${API_BASE_URL}/api/MonthlySummary?CompCode=${compCode}&type=sales`  // Monthly data (12 months)
          ];
          break;
        default:
          throw new Error('Unknown interval selected');
      }

      // Fetch all the APIs
      const fetchPromises = apiUrls.map(url =>
        fetch(`${url}&_=${new Date().getTime()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'UserId': userId,
            'Authorization': `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        })
      );

      const responses = await Promise.all(fetchPromises);
      clearTimeout(timeoutId);

      // Process responses
      const responseBodies = await Promise.all(responses.map(response => response.json()));

      // Check for errors or no data
      if (responses.some(response => !response.ok)) {
        throw new Error('Error fetching one or more APIs');
      }

      // Combine data from all APIs based on the selected interval
      let combinedData = [];
      responseBodies.forEach(responseBody => {
        if (responseBody && responseBody.length > 0) {
          combinedData = [...combinedData, ...responseBody];
        }
      });

      if (combinedData.length === 0) {
        setError('No data available');
      } else {
        setData(combinedData); // Set fetched data to the state
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The request timed out. Please try again.');
      } else {
        setError('Error fetching data, please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(interval);
  }, [interval, fetchData]);



  if (loading) {
    return (
      <div className="loader-container">
        <Lottie animationData={loaderAnimation} loop={true} autoplay={true} style={{ height: 100, width: 100 }} />
      </div>
    );
  }

  if (error) return <div>{error}</div>;

  // Function to map full weekday name to short format (e.g., Monday -> Mon)
  const getShortDayName = (fullDayName) => {
    const dayMap = {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    };
    return dayMap[fullDayName] || fullDayName;
  };

  // Map the 'ldate' (full weekday names) to short day names for daily data
  const labels = data.map(item => getShortDayName(item.ldate));

  const amounts = data.map(item => isNaN(item.amt) ? 0 : item.amt);   // Sanitize purchase amounts

  const formatNumberWithCommas = (number) => {
    if (number === undefined || number === null) {
      return ''; // Return empty if value is invalid
    }
    return number.toLocaleString(); // Apply number formatting
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Sales',
        data: amounts,
        backgroundColor: 'rgba(99, 102, 241, 0.2)', // Indigo-500 with opacity
        borderColor: 'rgba(99, 102, 241, 1)', // Indigo-500
        borderWidth: 2,
        borderRadius: 4,
        hoverBackgroundColor: 'rgba(99, 102, 241, 0.4)',
        type: 'bar',
        datalabels: {
          align: 'top',
          anchor: 'end',
          font: {
            weight: 'bold',
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: '#64748b', // Slate-500
          formatter: (value) => value > 0 ? formatNumberWithCommas(value) : '',
          display: 'auto'
        },
      },
      {
        label: 'Trend',
        data: amounts,
        borderColor: 'rgba(244, 63, 94, 1)', // Rose-500
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        tension: 0.4,
        fill: true,
        type: 'line',
        pointRadius: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: 'rgba(244, 63, 94, 1)',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        borderWidth: 3,
        datalabels: {
          display: false,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `Sales Summary (${interval.charAt(0).toUpperCase() + interval.slice(1)})`,
        font: {
          size: 14,
          weight: '600',
          family: "'Inter', sans-serif",
        },
        color: '#334155', // Slate-700
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 10,
        boxPadding: 4,
        usePointStyle: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(context.parsed.y);
            }
            return label;
          }
        }
      },
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          color: '#64748b'
        }
      },
      datalabels: {
        display: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f1f5f9',
          borderDash: [5, 5],
          drawBorder: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: '#64748b',
          callback: function (value) {
            if (value >= 10000000) return (value / 10000000).toFixed(1) + 'Cr';
            if (value >= 100000) return (value / 100000).toFixed(1) + 'L';
            if (value >= 1000) return (value / 1000).toFixed(1) + 'k';
            return value;
          }
        },
      },
    },
  };

  return (
    <div className="w-full h-full min-h-[300px] relative">
      <div className="flex justify-between items-center mb-4">
        <div className="flex-1"></div>
      </div>

      <div className="relative h-[250px] w-full">
        <Bar
          data={chartData}
          options={options}
        />
      </div>
    </div>
  );
};

export default SaleChart;
